import React, { Suspense, useEffect } from "react";
import PageMeta from "./components/PageMeta";
import MetaPixelPageViews from "./components/MetaPixelPageViews";
import ScrollToTop from "./components/ScrollToTop";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import { useWorkspaces } from "./hooks/useWorkspaces";
import Navbar from "./components/NavBar";
import AuctionSubscriptionGate from "./components/AuctionSubscriptionGate";

import { animated, useSpring } from "@react-spring/web";
import "./App.css";
import { useToast } from "./components/Toast/ToastContext";
import { useActiveCurrency } from "./services/currency";
// import { setGlobalNotifier } from "./store/axiosInstance";
// import Footer from "./components/Footer";
import styled from "styled-components";
import Inventory from "./screen/Inventory/InventoryPage";
import { useDispatch, useSelector } from "react-redux";
import { showLoginModal } from "./store/auth/login/actions";
import { setLastRoute } from "./store/routes/actions";
import { RootState } from "./store/store";
import InventoryImportPage from "./screen/Inventory/InventoryImportPage";
import Homepage from "./screen/Home/Home";
import FinanceDashboard from "./screen/Finance/FinanceDashboard";
import SalesTaxReportsPage from "./screen/Finance/SalesTaxReportsPage";
import NewPasswordContainer from "./screen/ResetPassword/NewPasswordContainer";
import PricingPage from "./screen/PricingPage";
import Billing from "./screen/Settings/Billing";
import SettingsPage from "./screen/Settings/SettingsPage";
import PrivacyPolicy from "./screen/Privacy/PrivacyPolicy";
import TermsAndConditions from "./screen/Terms/TermsAndConditions";
import EULA from "./screen/Eula/EULA";
import SupportPage from "./screen/Support/SupportPage";
import EventFinancialReport from "./screen/Finance/EventFinancialReport";
import MobileAppFAQ from "./screen/Support/MobileAppFAQ";
import DevNotificationsPage from "./screen/Dev/NotificationsTestPage";
import AnalyticsDashboard from "./screen/Admin/AnalyticsDashboard";
import EventsPage from "./screen/Events/EventsPage";
import EventInfoPage from "./screen/Events/EventInfoPage";
import EventDiscoveryPage from "./screen/EventDiscovery/EventDiscoveryPage";
import { canUseEventDiscovery } from "./screen/EventDiscovery/eventDiscoveryAccess";
import EventOpportunityDetailPage from "./screen/EventDiscovery/EventOpportunityDetailPage";
import LiveAuctionsPage from "./screen/LiveAuctions/LiveAuctionsPage";
import ReportsPage from "./screen/Reports/ReportsPage";
import OtherPage from "./screen/Other/OtherPage";
import BoothLayoutPlanner from "./screen/Tools/BoothLayoutPlanner";

const PdfEditorPage = React.lazy(
  () => import("./screen/PdfEditor/PdfEditorPage"),
);

const PrivateRoute = ({
  token,
  children,
}: {
  token: string | null;
  children: JSX.Element;
}) => {
  return token ? children : <Navigate to="/" />;
};

// Like PrivateRoute, but a logged-out visitor gets the login modal over the
// home page instead of a silent redirect, and lands on `path` after signing in.
const LoginPromptRoute = ({
  token,
  path,
  children,
}: {
  token: string | null;
  path: string;
  children: JSX.Element;
}) => {
  const dispatch = useDispatch();
  useEffect(() => {
    if (token) return;
    dispatch(showLoginModal());
    dispatch(setLastRoute(path));
  }, [token, path, dispatch]);
  return token ? children : <Navigate to="/" replace />;
};
// Pages an employee can't open while working in their employer's business;
// they get Events, and can switch to their own business for the rest.
const EMPLOYEE_BLOCKED_PREFIXES = [
  "/inventory",
  "/import",
  "/discover",
  "/live-auctions",
  "/tools",
  "/reports",
  "/finance",
  "/pdf-editor",
  "/billing",
  "/settings",
  "/admin",
  "/dev",
];

const EmployeeRouteGuard = ({ token }: { token: string | null }) => {
  const location = useLocation();
  const { active } = useWorkspaces(!!token);
  const blocked =
    active?.role === "employee" &&
    EMPLOYEE_BLOCKED_PREFIXES.some(
      (prefix) =>
        location.pathname === prefix ||
        location.pathname.startsWith(`${prefix}/`),
    );
  return blocked ? <Navigate to="/events" replace /> : null;
};

function App() {
  const token = useSelector((state: RootState) => state.auth.token);
  const user = useSelector((state: RootState) => state.auth.user);
  const isAdminEmail =
    String(user?.email || "").toLowerCase() === "beelgulzar@gmail.com";
  const discoveryEnabled = canUseEventDiscovery(user);

  useEffect(() => {
    if (!token) localStorage.clear();
  }, [token]);

  useToast();
  // Re-renders every page with the new symbol when the currency changes.
  useActiveCurrency();
  const styles = useSpring({
    loop: true,
    to: [
      { backgroundPositionX: "100%", config: { duration: 10000 } },
      { backgroundPositionX: "0%", config: { duration: 10000 } },
    ],
    from: { backgroundPositionX: "0%" },
  });

  // useEffect(() => {
  //   setGlobalNotifier((msg, type = "error") => {
  //     notify(msg, { type: type });
  //   });
  // }, [notify]);

  return (
    <AppContainer>
      <animated.div
        style={{
          backgroundImage: "linear-gradient(62deg, #8ec5fc, #e0c3fc)",
          backgroundSize: "200% 100%",
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          ...styles,
        }}
      >
        <Router>
          <PageMeta />
          <MetaPixelPageViews />
          <ScrollToTop />
          <EmployeeRouteGuard token={token} />
          <MainContent>
            <Navbar />
            <Routes>
              {/* <Route path="/login" element={<Login />} /> */}

              <Route
                path="/inventory"
                element={
                  <PrivateRoute token={token}>
                    <Inventory />
                  </PrivateRoute>
                }
              />
            </Routes>
            <Routes>
              {/* <Route path="/login" element={<Login />} /> */}

              <Route
                path="/import"
                element={
                  <LoginPromptRoute token={token} path="/import">
                    <InventoryImportPage />
                  </LoginPromptRoute>
                }
              />
              <Route
                path="/events"
                element={
                  <PrivateRoute token={token}>
                    <EventsPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/events/:eventId"
                element={
                  <PrivateRoute token={token}>
                    <EventInfoPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/discover"
                element={
                  discoveryEnabled ? (
                    <PrivateRoute token={token}>
                      <EventDiscoveryPage />
                    </PrivateRoute>
                  ) : (
                    <Navigate to="/events" replace />
                  )
                }
              />
              <Route
                path="/live-auctions"
                element={<PrivateRoute token={token}><AuctionSubscriptionGate><LiveAuctionsPage /></AuctionSubscriptionGate></PrivateRoute>}
              />
              <Route
                path="/live-auctions/:sessionId"
                element={<PrivateRoute token={token}><AuctionSubscriptionGate><LiveAuctionsPage /></AuctionSubscriptionGate></PrivateRoute>}
              />
              <Route path="/other" element={<Navigate to="/tools" replace />} />
              <Route
                path="/tools"
                element={<OtherPage />}
              />
              <Route
                path="/tools/booth-layout"
                element={<BoothLayoutPlanner />}
              />
              <Route
                path="/discover/:opportunityId"
                element={
                  discoveryEnabled ? (
                    <PrivateRoute token={token}>
                      <EventOpportunityDetailPage />
                    </PrivateRoute>
                  ) : (
                    <Navigate to="/events" replace />
                  )
                }
              />
              <Route
                path="/reports"
                element={
                  <PrivateRoute token={token}>
                    <ReportsPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/finance"
                element={
                  <PrivateRoute token={token}>
                    <FinanceDashboard />
                  </PrivateRoute>
                }
              />
              <Route
                path="/finance/tax-reports"
                element={
                  <PrivateRoute token={token}>
                    <SalesTaxReportsPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/finance/event/:eventId"
                element={
                  <PrivateRoute token={token}>
                    <EventFinancialReport />
                  </PrivateRoute>
                }
              />
              <Route
                path="/pdf-editor"
                element={
                  <PrivateRoute token={token}>
                    <Suspense fallback={null}>
                      <PdfEditorPage />
                    </Suspense>
                  </PrivateRoute>
                }
              />
              {/* Support (public) */}
              <Route path="/support" element={<SupportPage />} />
            </Routes>

            <Routes>
              {/* <Route path="/login" element={<Login />} /> */}

              <Route
                path="/reset-password/:token"
                element={<NewPasswordContainer />}
              />
              <Route path="/billing" element={<Billing />} />
              <Route
                path="/settings"
                element={
                  <PrivateRoute token={token}>
                    <SettingsPage />
                  </PrivateRoute>
                }
              />
              <Route path="/pricing" element={<PricingPage />} />

              <Route path="/" element={<Homepage />} />

              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route
                path="/terms-and-condition"
                element={<TermsAndConditions />}
              />
              <Route path="/eula" element={<EULA />} />
              <Route path="/faq" element={<MobileAppFAQ />} />
              <Route
                path="/admin/analytics"
                element={
                  <PrivateRoute token={token}>
                    {isAdminEmail ? (
                      <AnalyticsDashboard />
                    ) : (
                      <Navigate to="/" />
                    )}
                  </PrivateRoute>
                }
              />
              {process.env.NODE_ENV === "development" && (
                <Route
                  path="/dev/notifications"
                  element={<DevNotificationsPage />}
                />
              )}
            </Routes>
          </MainContent>
        </Router>
        {/* <Footer /> */}
      </animated.div>
    </AppContainer>
  );
}

const AppContainer = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
`;

const MainContent = styled.main`
  flex: 1;
  display: flex;
  flex-direction: column;
  padding-top: 56px; /* match Navbar height (use 64px if needed) */
`;

export default App;
